import React, { useState } from 'react';
import PageBanner from '../../components/section/PageBanner';
import { sheduleLink } from '../../constant/Constant';
import ImageTextSection from './components/sections/ImageTextSection';
import bookCoverImage from '../../image/next-method-book-cover.png';
import TextSection from './components/sections/TextSection';
import NextMethodOverview from './components/sections/NextMethodOverview';
import ROICalculator from './components/tools/ROICalculator';
import PlanningAssessment from './components/tools/PlanningAssessment';
import CandidatePoolGrid from './components/tools/CandidatePoolGrid';
import BookReviewForm from './components/tools/BookReviewForm';
import RequestCopyForm from './components/tools/RequestCopyForm';
import ToolCard from './components/common/ToolCard';
import Modal from './components/common/Modal';

const NextMethod = () => {
  const [isROIModalOpen, setIsROIModalOpen] = useState(false);
  const [isAssessmentModalOpen, setIsAssessmentModalOpen] = useState(false);
  const [isCandidatePoolModalOpen, setIsCandidatePoolModalOpen] = useState(false);
  const [isBookReviewModalOpen, setIsBookReviewModalOpen] = useState(false);
  const [isRequestCopyModalOpen, setIsRequestCopyModalOpen] = useState(false);

  return (
    <>
      <PageBanner title={<>The NEXT Method<sup>™</sup></>} />

      {/* Request a Copy Button - Top */}
      <section className="py-8 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <button
              onClick={() => setIsRequestCopyModalOpen(true)}
              className="inline-flex justify-center items-center gap-2 bg-dark-dark-teal-400 hover:bg-dark-dark-teal-600 text-white rounded-full text-base h-12 py-0 px-8 transition-all duration-200 cursor-pointer font-semibold"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Request a Copy
            </button>
          </div>
        </div>
      </section>

      <ImageTextSection
        image={bookCoverImage}
        imageAlt="The NEXT Method - Leadership Continuity for Credit Unions book cover"
        title="SuccessionNow: The NEXT Method™"
        imageAction={
          <button
            onClick={() => setIsBookReviewModalOpen(true)}
            className="text-dark-teal-600 hover:text-dark-dark-teal-400 text-sm font-medium hover:underline transition-all duration-200 flex items-center gap-1"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
            Leave a Review
          </button>
        }
      >
        <p className="mb-4">
          The NEXT Method provides systematic, role-specific succession planning designed specifically for credit union environments. Rather than attempting to plan succession for every position simultaneously—which spreads resources too thin and produces no actual results—the NEXT Method focuses on one critical role at a time, building genuine succession readiness through four integrated phases:
        </p>
        <p className="mb-4">
          <strong>Navigate:</strong> Identify your succession planning target role using systematic selection criteria that balance immediate needs with development feasibility.
        </p>
        <p className="mb-4">
          <strong>Evaluate:</strong> Discover and assess future leaders through a 4-step evaluation process that measures cultural fit, competency gaps, readiness levels, and success predictors.
        </p>
        <p className="mb-4">
          <strong>Xccelerate:</strong> Create targeted preparation plans that address specific competency gaps and implement systematic readiness assessment to ensure development produces actual capability.
        </p>
        <p>
          <strong>Transition:</strong> Make evidence-based succession recommendations across four time horizons, from emergency succession to long-term pipeline building.
        </p>
      </ImageTextSection>
      <NextMethodOverview />
      <TextSection
        title="What It's About"
        collapsible
      >
        <p className="mb-4">
          "The NEXT Method" is a practical guidebook for credit union leaders who need to build a reliable succession plan but don't know where to start—or who've tried generic corporate approaches that didn't fit their cooperative structure.
        </p>
        <p className="mb-4">
          Drawing on more than 30 years of hands-on consulting with credit union boards and executives, Jerry P. Nelson presents a four-phase framework (Navigate, Evaluate, Xccelerate, Transition) that tackles succession one critical role at a time rather than overwhelming organizations with enterprise-wide planning that stalls out. The book opens with a cautionary case study of a $200 million credit union that lost $5 million in value when its CEO faced a sudden health crisis without a plan in place—illustrating exactly what's at stake when succession is treated as an afterthought.
        </p>
        <p className="mb-4">
          Throughout the book, Jerry explains why off-the-shelf corporate methodologies fall short for credit unions, whose volunteer governance, member-owner accountability, and cooperative culture demand a specialized approach. He then walks readers through actionable tools—including a 90-day implementation roadmap, competency frameworks tailored to credit union leadership, candidate readiness assessments, and board governance templates designed to meet NCUA expectations.
        </p>
        <p>
          The result is a step-by-step resource that moves succession planning from abstract priority to documented, sustainable process.
        </p>
      </TextSection>
      <TextSection title="A Case Study: The $5 Million Leadership Crisis" collapsible>
        <p className="mb-4 italic">
          The following composite case represents common elements observed across multiple credit union transitions. While the scenario reflects real challenges and typical costs, specific organizations and individuals have been combined to illustrate industry-wide patterns.
        </p>
        <p className="mb-4">
          <strong>Mountain View Community Credit Union</strong> ($200 million assets) demonstrates the true cost of poor succession planning.
        </p>

        <h3 className="font-bold text-gray-900 mb-2">The Setup</h3>
        <ul className="list-disc ml-6 mb-4">
          <li>18-year CEO, beloved by members and community</li>
          <li>Age 63, talking retirement for three years</li>
          <li>Board kept asking him to stay "one more year"</li>
          <li>No succession plan</li>
        </ul>

        <h3 className="font-bold text-gray-900 mb-2">The Crisis</h3>
        <p className="mb-4">
          March 2019: CEO has heart attack, doctors recommend immediate retirement. Board has six weeks to find replacement.
        </p>

        <h3 className="font-bold text-gray-900 mb-2">Nine-Month Interim Period Damage</h3>
        <p className="font-bold mb-2">Operational Impact:</p>
        <ul className="list-disc ml-6 mb-4">
          <li>CFO promoted to interim CEO (no strategic experience, limited member skills)</li>
          <li>Member satisfaction scores plummeted</li>
          <li>Strategic initiatives paralyzed</li>
          <li>Staff operated without clear direction</li>
        </ul>

        <p className="font-bold mb-2">Market Response:</p>
        <ul className="list-disc ml-6 mb-4">
          <li>Competitors launched aggressive campaigns targeting members</li>
          <li>Neighboring credit union hired away two key lenders</li>
          <li>Market share dropped 30% in core geographic area</li>
          <li>New member acquisition fell 40%</li>
        </ul>

        <h3 className="font-bold text-gray-900 mb-2">Recovery Attempts</h3>
        <p className="mb-2">New CEO hired from out-of-state inherited:</p>
        <ul className="list-disc ml-6 mb-4">
          <li>Demoralized staff</li>
          <li>Eroded member trust</li>
          <li>Intense competitive pressure</li>
          <li>Damaged board relationships</li>
        </ul>

        <h3 className="font-bold text-gray-900 mb-2">Total Cost: $5 Million</h3>
        <ul className="list-disc ml-6 mb-4">
          <li>Lost revenue: $3.2 million</li>
          <li>Increased operational costs: $900,000</li>
          <li>Search and transition costs: $300,000</li>
          <li>Member acquisition to replace losses: $600,000</li>
        </ul>
        <p className="mb-4">
          <strong>Intangible costs:</strong> Damaged reputation, lost expertise, reduced loyalty, board credibility issues.
        </p>

        <h3 className="font-bold text-gray-900 mb-2">The Lesson</h3>
        <p>
          This wasn't caused by bad decisions—it was caused by good people without systematic succession planning. The heart attack was unpredictable; its impact was entirely preventable.
        </p>
      </TextSection>
      {/* Tools Section */}
      <section className="pt-4 md:pt-6 pb-12 md:pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-8 md:mb-12 font-PlusJakartaSans">
              Tools
            </h2>
            <div className="space-y-4">
            <ToolCard
              title="Succession Planning Costs"
              subtitle="Discover the true cost of inadequate succession planning in under 10 minutes"
              onClick={() => setIsROIModalOpen(true)}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              }
            />
            <ToolCard
              title="Succession Planning Assessment"
              subtitle="Evaluate your current succession planning practices with a 15-minute assessment"
              onClick={() => setIsAssessmentModalOpen(true)}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              }
            />
            <ToolCard
              title="Candidate Pool Framework"
              subtitle="Visual guide for developing primary and secondary candidate pools with cultural fit screening"
              onClick={() => setIsCandidatePoolModalOpen(true)}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              }
            />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Combined Buttons */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8 md:gap-4">
            {/* Request a Copy */}
            <div className="flex-1 text-center">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 font-PlusJakartaSans">
                Get Your Copy Today
              </h3>
              <p className="text-sm md:text-base text-gray-600 mb-4">
                Download The NEXT Method
              </p>
              <button
                onClick={() => setIsRequestCopyModalOpen(true)}
                className="inline-flex justify-center items-center gap-2 bg-dark-dark-teal-400 hover:bg-dark-dark-teal-600 text-white rounded-full text-base h-12 py-0 px-6 transition-all duration-200 cursor-pointer font-semibold"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Request a Copy
              </button>
            </div>

            {/* Leave a Review */}
            <div className="flex-1 text-center">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 font-PlusJakartaSans">
                Have you read the book?
              </h3>
              <p className="text-sm md:text-base text-gray-600 mb-4">
                Share your thoughts
              </p>
              <button
                onClick={() => setIsBookReviewModalOpen(true)}
                className="inline-flex justify-center items-center gap-2 bg-dark-dark-teal-400 hover:bg-dark-dark-teal-600 text-white rounded-full text-base h-12 py-0 px-6 transition-all duration-200 cursor-pointer font-semibold"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
                Leave a Review
              </button>
            </div>

            {/* Request a Demo */}
            <div className="flex-1 text-center">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 font-PlusJakartaSans">
                Ready to Automate?
              </h3>
              <p className="text-sm md:text-base text-gray-600 mb-4">
                Get a tour of SuccessionNow
              </p>
              <a
                href={sheduleLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex justify-center items-center gap-2 bg-dark-dark-teal-400 hover:bg-dark-dark-teal-600 text-white rounded-full text-base h-12 py-0 px-6 transition-all duration-200 cursor-pointer font-semibold"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Request a Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator Modal */}
      <Modal isOpen={isROIModalOpen} onClose={() => setIsROIModalOpen(false)}>
        <ROICalculator />
      </Modal>

      {/* Planning Assessment Modal */}
      <Modal isOpen={isAssessmentModalOpen} onClose={() => setIsAssessmentModalOpen(false)}>
        <PlanningAssessment />
      </Modal>

      {/* Candidate Pool Grid Modal */}
      <Modal isOpen={isCandidatePoolModalOpen} onClose={() => setIsCandidatePoolModalOpen(false)}>
        <CandidatePoolGrid />
      </Modal>

      {/* Book Review Form Modal */}
      <Modal isOpen={isBookReviewModalOpen} onClose={() => setIsBookReviewModalOpen(false)}>
        <BookReviewForm />
      </Modal>

      {/* Request Copy Form Modal */}
      <Modal isOpen={isRequestCopyModalOpen} onClose={() => setIsRequestCopyModalOpen(false)}>
        <RequestCopyForm />
      </Modal>
    </>
  );
};

export default NextMethod;
